import ImportExportIcon from '@mui/icons-material/ImportExport';
import SettingsIcon from '@mui/icons-material/Settings';
import React, { Children, useEffect, useState } from 'react';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import { DemeterMarket, DemeterTableDefinitionCommodityModel, DemeterTableDefinitionType, DemeterUserStoreType } from '../../../Generated/Raven-Demeter';
import useTableDefinitionsByCommodityApi from '../../Apis/Hooks/useTableDefinitionsCommodityApiHook';
import useUserStoreApi from '../../Apis/Hooks/useUserStoreApiHook';
import useLanguage from '../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../Services/Tracking/TrackingService';
import IconButton from '../Form/Buttons/IconButton';
import MenuSettingsModal from '../Modals/ProductsModal/MenuSettingsModal';
import useSearchParameters from '../Navigation/Hooks/useSearchParametersHook';
import BreadCrumb from './BreadCrumb';
import styles from './MenuFileSelectors.module.scss';
import { MenuTypeForSearchParameter } from './MenuSelectorItem';

interface MenuSelectorWithFlipWrapperProps {
    breadCrumbs?: string;
    children: JSX.Element;
    isLoading?: boolean;
    rightSideOfBreadcrumbsComponent?: JSX.Element;
    tableDefinitionType: DemeterTableDefinitionType;
    handleMenuFlip: () => void;
    handleMenuUpdate: (settings: Record<string, string | boolean | DemeterTableDefinitionCommodityModel[]>) => void;
    track: TrackingEvent;
}

const MenuSelectorWithFlipWrapper: React.FC<MenuSelectorWithFlipWrapperProps> = (props: MenuSelectorWithFlipWrapperProps) => {
    const [translations] = useLanguage();
    const [searchParameters] = useSearchParameters();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [userStoreValue] = useUserStoreApi(DemeterUserStoreType.DisplaySettings);
    const tableDefinitionsByCommodity = useTableDefinitionsByCommodityApi(DemeterTableDefinitionType.AllFundamentalsAndPrices, DemeterMarket.All);

    const handleFlip = () => {
        props.handleMenuFlip();
    };

    const handleIsModalOpen = () => {
        setIsModalOpen((previous) => !previous);
    };

    const handleMenuSettingsUpdate = (settings: Record<string, string | boolean | DemeterTableDefinitionCommodityModel[]>) => {
        props.handleMenuUpdate(settings);
    };

    useEffect(() => {
        if (!userStoreValue?.userStore?.value || !tableDefinitionsByCommodity) {
            return;
        }

        const userHasValidSelectedCommodities =
            userStoreValue?.userStore?.value[applicationConstants.MenuSettingsSelectedCommoditiesKey]?.length &&
            userStoreValue?.userStore?.value[applicationConstants.MenuSettingsUserHasSetCommodities];

        props.handleMenuUpdate({
            [applicationConstants.MenuSettingsUserAlreadyAcknowledged]: true,
            [applicationConstants.MenuSettingsTopLevelKey]:
                userStoreValue?.userStore?.value[applicationConstants.MenuSettingsTopLevelKey] ??
                searchParameters.topMenuType ??
                MenuTypeForSearchParameter.region,
            [applicationConstants.MenuSettingsUserHasSetCommodities]: !userHasValidSelectedCommodities
                ? false
                : userStoreValue?.userStore?.value[applicationConstants.MenuSettingsUserHasSetCommodities],
            [applicationConstants.MenuSettingsSelectedCommoditiesKey]: !userHasValidSelectedCommodities
                ? []
                : userStoreValue?.userStore?.value[applicationConstants.MenuSettingsSelectedCommoditiesKey]?.filter(
                      (x: DemeterTableDefinitionCommodityModel) => x.tableDefinitionType === DemeterTableDefinitionType.AllFundamentalsAndPrices,
                  ),
        } as Record<string, string | boolean | DemeterTableDefinitionCommodityModel[]>);
    }, [userStoreValue, tableDefinitionsByCommodity]);

    return (
        <>
            <div>
                {!props.isLoading && (
                    <div className={styles.menu_flip_wrapper_icons}>
                        <IconButton
                            tooltip={translations.menu.tooltip.flipRegionsAndCommodities}
                            handleClick={handleFlip}
                            track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}FlipClicked` }}
                        >
                            <ImportExportIcon />
                        </IconButton>
                        <IconButton
                            tooltip={translations.menu.tooltip.settings}
                            handleClick={handleIsModalOpen}
                            track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}SettingsClicked` }}
                        >
                            <SettingsIcon />
                        </IconButton>
                    </div>
                )}
                <div className={styles.menu_flip_wrapper_menu}>{Children.only(props.children)}</div>
            </div>
            <div className={styles.menu_flip_wrapper_bottom_row}>
                <div className={styles.menu_flip_wrapper_left_side}>
                    <BreadCrumb breadCrumbs={props.breadCrumbs} />
                </div>
                <div className={styles.menu_flip_wrapper_right_side}>
                    {props.rightSideOfBreadcrumbsComponent && <div>{props.rightSideOfBreadcrumbsComponent}</div>}
                </div>
            </div>
            <MenuSettingsModal
                open={isModalOpen}
                tableDefinitionType={props.tableDefinitionType}
                handleClose={handleIsModalOpen}
                setMenuSettings={handleMenuSettingsUpdate}
                track={{ eventCategory: props.track.eventCategory, eventAction: 'ModalMenuSettings' }}
            />
        </>
    );
};

export default MenuSelectorWithFlipWrapper;
